import React from 'react';
import {
  Shield,
  Info,
  Database,
  Settings,
  Lock,
  Share2,
  Cookie,
  Link2,
  FileText,
  User,
  Baby,
  RefreshCw,
  Mail
} from 'lucide-react';
import Layout from '../component/layout/Layout';

export default function PrivacyPolicyPage() {
  const [isDesktop, setIsDesktop] = React.useState(window.innerWidth >= 1024);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 640);

  React.useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const iconMap = {
    'Introduction': Info,
    'Information We Collect': Database,
    'How We Use Your Information': Settings,
    'How We Protect Your Information': Lock,
    'Sharing of Information': Share2,
    'Cookies and Tracking Technologies': Cookie,
    'Third-Party Links': Link2,
    'Data Retention': FileText,
    'Your Rights': User,
    'Children\'s Privacy': Baby,
    'Changes to the Privacy Policy': RefreshCw,
    'Contact Information': Mail,
  };

  const privacyPolicyContent = [
    {
      title: "Introduction",
      description: "This Privacy Policy explains how we collect, use, and protect your personal information when you use our website and services. By using our website and purchasing courses, you consent to the practices outlined in this policy."
    },
    {
      title: "Information We Collect",
      description: "We collect personal details such as your name, email address, phone number, and billing information when you create an account, make a purchase, or contact us for support. We also collect usage data, such as pages visited and interactions with the website, to improve the user experience."
    },
    {
      title: "How We Use Your Information",
      description: "We use your information to manage your account, process transactions, send notifications, and improve our services. This includes sending you updates, offers, and responding to queries. We also use data to prevent fraud, enforce our terms of service, and comply with legal obligations."
    },
    {
      title: "How We Protect Your Information",
      description: "We implement industry-standard security measures, such as encryption and secure payment processing, to protect your personal data. We also work with trusted third-party service providers, such as Razorpay, who adhere to strict privacy and security standards."
    },
    {
      title: "Sharing of Information",
      description: "We do not sell or rent your personal information. However, we may share it with trusted service providers (e.g., payment processors) who assist in delivering our services. We may also disclose your information if required by law or to protect our legal rights."
    },
    {
      title: "Cookies and Tracking Technologies",
      description: "We use cookies and similar technologies to enhance your experience on our website. Cookies are small files stored on your device to remember your preferences and track website usage. You can control cookie settings through your browser, but disabling cookies may affect your site experience."
    },
    {
      title: "Third-Party Links",
      description: "Our website may contain links to external websites. We are not responsible for the privacy practices of these third-party sites. We encourage you to review the privacy policies of any external websites you visit."
    },
    {
      title: "Data Retention",
      description: "We retain your personal information as long as necessary to fulfill the purposes outlined in this policy. If you wish to delete your account or personal data, please contact our support team."
    },
    {
      title: "Your Rights",
      description: "You have the right to access, correct, or delete your personal information. You can also opt-out of receiving marketing communications. To exercise your rights, contact us at support@skillserve.in."
    },
    {
      title: "Children's Privacy",
      description: "Our website and services are not intended for individuals under 18. We do not knowingly collect personal information from children. If we become aware of such data collection, we will take steps to delete it."
    },
    {
      title: "Changes to the Privacy Policy",
      description: "We may update this Privacy Policy periodically. Any changes will be posted here, with the date of the last update indicated. We encourage you to review this policy regularly to stay informed."
    },
    {
      title: "Contact Information",
      description: "If you have any questions or concerns regarding this Privacy Policy, please contact us at:\nEmail: support@skillserve.in\nPhone: +91-9484794843\nAddress: Sector 33, Gurugram - 122001"
    }
  ];

  const pageWrapStyle = {
    width: '100%',
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
    padding: isDesktop ? '80px 24px' : isMobile ? '60px 20px' : '70px 24px',
  };

  const containerStyle = {
    maxWidth: '1000px',
    margin: '0 auto',
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: isDesktop ? '60px' : '40px',
    padding: isDesktop ? '40px' : '30px',
    background: '#ffffff',
    borderRadius: '20px',
    boxShadow: '0 8px 24px rgba(11, 49, 97, 0.06)',
    border: '1px solid #e2e8f0',
  };

  const shieldIconWrapperStyle = {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #0b3161 0%, #1e4a7a 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  };

  const titleStyle = {
    fontFamily: "'Roboto Slab', serif",
    fontSize: isDesktop ? '2.8rem' : isMobile ? '2rem' : '2.4rem',
    fontWeight: 700,
    color: '#081426',
    margin: '0 0 12px 0',
    lineHeight: 1.2,
  };

  const effectiveDateStyle = {
    fontSize: '0.95rem',
    color: '#64748b',
    fontWeight: 500,
  };

  const contentStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  };

  const sectionStyle = {
    background: '#ffffff',
    borderRadius: '16px',
    padding: isDesktop ? '32px' : isMobile ? '20px' : '28px',
    boxShadow: '0 4px 16px rgba(11, 49, 97, 0.04)',
    border: '1px solid #e2e8f0',
    transition: 'all 0.3s ease',
  };

  const sectionHeaderStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '16px',
  };

  const iconWrapperStyle = {
    width: '44px',
    height: '44px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  const sectionTitleStyle = {
    fontSize: isDesktop ? '1.35rem' : '1.2rem',
    fontWeight: 700,
    color: '#0b3161',
    margin: 0,
    lineHeight: 1.3,
  };

  const sectionDescStyle = {
    fontSize: '1rem',
    lineHeight: 1.8,
    color: '#475569',
    margin: 0,
    whiteSpace: 'pre-line',
  };

  const footerNoteStyle = {
    marginTop: '40px',
    padding: '24px',
    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    borderRadius: '12px',
    border: '1px solid #fbbf24',
    textAlign: 'center',
  };

  const footerNoteTextStyle = {
    fontSize: '0.95rem',
    color: '#78350f',
    fontWeight: 600,
    margin: 0,
  };

  return (
    <><Layout>
      <div style={pageWrapStyle}>
        <div style={containerStyle}>
          {/* Header */}
          <div style={headerStyle}>
            <div style={shieldIconWrapperStyle}>
              <Shield size={40} color="#ffffff" />
            </div>
            <h1 style={titleStyle}>Privacy Policy</h1>
            <p style={effectiveDateStyle}>Effective Date: January 13, 2025</p>
          </div>

          {/* Content Sections */}
          <div style={contentStyle}>
            {privacyPolicyContent.map((section, index) => {
              const Icon = iconMap[section.title] || Info;

              return (
                <div key={index} style={sectionStyle}>
                  <div style={sectionHeaderStyle}>
                    <div style={iconWrapperStyle}>
                      <Icon size={22} color="#0b3161" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <h2 style={sectionTitleStyle}>{section.title}</h2>
                    </div>
                  </div>
                  <p style={sectionDescStyle}>{section.description}</p>
                </div>
              );
            })}
          </div>

          {/* Footer Note */}
          <div style={footerNoteStyle}>
            <p style={footerNoteTextStyle}>
              Your privacy is important to us. We are committed to protecting your personal information.
            </p>
          </div>
        </div>
      </div>
    </Layout></>
  );
}